import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import i18next from 'i18next';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8087';

// Initial state
const initialState = {
  selections: [],
  betType: 'single',
  systemConfig: {
    selectedTypes: [],
    amounts: {},
    checkedAll: false
  },
  amounts: {
    stake: 0,
    totalStake: 0,
    perCombination: 0
  },
  calculations: {
    totalOdds: 1,
    potentialWin: { min: 0, max: 0 },
    combinations: 0,
    systemDetails: []
  },
  validation: {
    status: 'idle',
    errors: [],
    warnings: [],
    limits: {}
  },
  meta: {
    ticketId: null,
    context: 'prematch',
    createdAt: null,
    isSubmitting: false
  }
};

// Action types
const ACTIONS = {
  ADD_SELECTION: 'ADD_SELECTION',
  REMOVE_SELECTION: 'REMOVE_SELECTION',
  UPDATE_SELECTION_ODDS: 'UPDATE_SELECTION_ODDS',
  CLEAR_SELECTIONS: 'CLEAR_SELECTIONS',
  SET_BET_TYPE: 'SET_BET_TYPE',
  SET_SYSTEM_CONFIG: 'SET_SYSTEM_CONFIG',
  SET_AMOUNT: 'SET_AMOUNT',
  SET_SYSTEM_AMOUNT: 'SET_SYSTEM_AMOUNT',
  UPDATE_CALCULATIONS: 'UPDATE_CALCULATIONS',
  SET_VALIDATION: 'SET_VALIDATION',
  SET_VALIDATION_STATUS: 'SET_VALIDATION_STATUS',
  SET_META: 'SET_META',
  RESET: 'RESET',
  LOAD_FROM_STORAGE: 'LOAD_FROM_STORAGE'
};

// Reducer
function betslipReducer(state, action) {
  switch (action.type) {
    case ACTIONS.ADD_SELECTION: {
      const existingIndex = state.selections.findIndex(
        s => s.eventId === action.payload.eventId && 
             s.market.id === action.payload.market.id &&
             s.outcome.id === action.payload.outcome.id
      );
      
      if (existingIndex >= 0) {
        return state;
      }
      
      const newSelections = [...state.selections, action.payload];
      const newBetType = newSelections.length === 1 ? 'single' : 
                         state.betType === 'single' ? 'multiple' : state.betType;
      
      return {
        ...state,
        selections: newSelections,
        betType: newBetType,
        meta: {
          ...state.meta,
          ticketId: state.meta.ticketId || uuidv4(),
          context: determineContext(newSelections)
        }
      };
    }
    
    case ACTIONS.REMOVE_SELECTION: {
      const newSelections = state.selections.filter(
        s => !(s.eventId === action.payload.eventId && 
               s.market.id === action.payload.marketId &&
               s.outcome.id === action.payload.outcomeId)
      );
      
      const newBetType = newSelections.length <= 1 ? 'single' : state.betType;
      
      return {
        ...state,
        selections: newSelections,
        betType: newBetType,
        meta: {
          ...state.meta,
          context: determineContext(newSelections),
          ticketId: newSelections.length === 0 ? null : state.meta.ticketId
        }
      };
    }
    
    case ACTIONS.UPDATE_SELECTION_ODDS: {
      const { eventId, marketId, outcomeId, newOdds } = action.payload;
      const newSelections = state.selections.map(s => {
        if (s.eventId === eventId && s.market.id === marketId && s.outcome.id === outcomeId) {
          return {
            ...s,
            outcome: { ...s.outcome, odds: newOdds }
          };
        }
        return s;
      });
      
      return { ...state, selections: newSelections };
    }
    
    case ACTIONS.CLEAR_SELECTIONS:
      return {
        ...initialState,
        validation: { ...initialState.validation, limits: state.validation.limits }
      };
    
    case ACTIONS.SET_BET_TYPE:
      return { ...state, betType: action.payload };
    
    case ACTIONS.SET_SYSTEM_CONFIG:
      return { 
        ...state, 
        systemConfig: { ...state.systemConfig, ...action.payload }
      };
    
    case ACTIONS.SET_AMOUNT:
      return {
        ...state,
        amounts: { ...state.amounts, ...action.payload }
      };
    
    case ACTIONS.SET_SYSTEM_AMOUNT: {
      const { type, amount } = action.payload;
      return {
        ...state,
        systemConfig: {
          ...state.systemConfig,
          amounts: { ...state.systemConfig.amounts, [type]: amount }
        }
      };
    }
    
    case ACTIONS.UPDATE_CALCULATIONS:
      return {
        ...state,
        calculations: { ...state.calculations, ...action.payload }
      };
    
    case ACTIONS.SET_VALIDATION:
      return {
        ...state,
        validation: { ...state.validation, ...action.payload }
      };
    
    case ACTIONS.SET_VALIDATION_STATUS:
      return {
        ...state,
        validation: { ...state.validation, status: action.payload }
      };
    
    case ACTIONS.SET_META:
      return {
        ...state,
        meta: { ...state.meta, ...action.payload }
      };
    
    case ACTIONS.RESET:
      return { ...initialState };
    
    case ACTIONS.LOAD_FROM_STORAGE:
      return { ...state, ...action.payload };
    
    default:
      return state;
  }
}

// Helper: Determine ticket context (prematch/live/hybrid)
function determineContext(selections) {
  if (!selections || selections.length === 0) return 'prematch';
  
  const hasLive = selections.some(s => s.context === 'live');
  const hasPrematch = selections.some(s => s.context === 'prematch');
  
  if (hasLive && hasPrematch) return 'hybrid';
  if (hasLive) return 'live';
  return 'prematch';
}

// Create context
const BetslipContext = createContext(null);

// Provider component
export function BetslipProvider({ children }) {
  const [state, dispatch] = useReducer(betslipReducer, initialState);

  // Load from sessionStorage on mount
  useEffect(() => {
    try {
      const savedBetslip = sessionStorage.getItem('betslip');
      if (savedBetslip) {
        const parsed = JSON.parse(savedBetslip);
        dispatch({ type: ACTIONS.LOAD_FROM_STORAGE, payload: parsed });
      }
    } catch (e) {
      console.warn('Failed to load betslip from storage:', e);
    }
  }, []);

  // Save to sessionStorage on state change
  useEffect(() => {
    if (state.selections.length > 0 || state.meta.ticketId) {
      try {
        sessionStorage.setItem('betslip', JSON.stringify({
          selections: state.selections,
          betType: state.betType,
          systemConfig: state.systemConfig,
          amounts: state.amounts,
          meta: state.meta
        }));
      } catch (e) {
        console.warn('Failed to save betslip to storage:', e);
      }
    }
  }, [state.selections, state.betType, state.systemConfig, state.amounts, state.meta]);

  // Actions
  const addSelection = useCallback((selection) => {
    const normalizedSelection = {
      id: `${selection.eventId}-${selection.market.id}-${selection.outcome.id}`,
      eventId: selection.eventId,
      eventName: selection.eventName,
      eventStartTime: selection.eventStartTime,
      context: selection.context || 'prematch',
      market: {
        id: selection.market.id,
        name: selection.market.name,
        type: selection.market.type || 'standard'
      },
      outcome: {
        id: selection.outcome.id,
        name: selection.outcome.name,
        odds: parseFloat(selection.outcome.odds) || 1,
        oddsAtSelection: parseFloat(selection.outcome.odds) || 1,
        sbv: selection.outcome.sbv || null
      },
      status: 'active',
      addedAt: new Date().toISOString()
    };
    
    dispatch({ type: ACTIONS.ADD_SELECTION, payload: normalizedSelection });
  }, []);

  const removeSelection = useCallback((eventId, marketId, outcomeId) => {
    dispatch({ 
      type: ACTIONS.REMOVE_SELECTION, 
      payload: { eventId, marketId, outcomeId }
    });
  }, []);

  const updateSelectionOdds = useCallback((eventId, marketId, outcomeId, newOdds) => {
    dispatch({
      type: ACTIONS.UPDATE_SELECTION_ODDS,
      payload: { eventId, marketId, outcomeId, newOdds }
    });
  }, []);

  const clearSelections = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_SELECTIONS });
    sessionStorage.removeItem('betslip');
  }, []);

  const setBetType = useCallback((betType) => {
    dispatch({ type: ACTIONS.SET_BET_TYPE, payload: betType });
  }, []);

  const setSystemConfig = useCallback((config) => {
    dispatch({ type: ACTIONS.SET_SYSTEM_CONFIG, payload: config });
  }, []);

  const setAmount = useCallback((amounts) => {
    dispatch({ type: ACTIONS.SET_AMOUNT, payload: amounts });
  }, []);

  const setSystemAmount = useCallback((type, amount) => {
    dispatch({ type: ACTIONS.SET_SYSTEM_AMOUNT, payload: { type, amount } });
  }, []);

  const updateCalculations = useCallback((calculations) => {
    dispatch({ type: ACTIONS.UPDATE_CALCULATIONS, payload: calculations });
  }, []);

  const setValidation = useCallback((validation) => {
    dispatch({ type: ACTIONS.SET_VALIDATION, payload: validation });
  }, []);

  // Validate ticket with backend
  const validateTicket = useCallback(async () => {
    // #region agent log
    // #endregion
    if (state.selections.length === 0) {
      setValidation({ status: 'idle', errors: [], warnings: [] });
      return { valid: true };
    }

    dispatch({ type: ACTIONS.SET_VALIDATION_STATUS, payload: 'validating' });

    try {
      const userId = localStorage.getItem('userId') || 
                     JSON.parse(localStorage.getItem('CLIENT_SESSION') || '{}')?.userId;
      
      const response = await fetch(`${API_URL}/api/bets/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        },
        body: JSON.stringify({
          selections: state.selections,
          betType: state.betType,
          amounts: state.amounts,
          systemConfig: state.systemConfig,
          userId,
          context: state.meta.context
        })
      });

      const result = await response.json();

      if (response.ok) {
        setValidation({
          status: result.valid ? 'valid' : 'invalid',
          errors: result.errors || [],
          warnings: result.warnings || [],
          limits: result.limits || {}
        });

        if (result.calculations) {
          updateCalculations(result.calculations);
        }

        return result;
      } else {
        setValidation({
          status: 'invalid',
          errors: [{ code: 'SERVER_ERROR', message: result.error || i18next.t('Errore di validazione') }],
          warnings: []
        });
        return { valid: false, errors: [{ code: 'SERVER_ERROR', message: result.error }] };
      }
    } catch (error) {
      console.error('Validation error:', error);
      setValidation({
        status: 'invalid',
        errors: [{ code: 'NETWORK_ERROR', message: i18next.t('Errore di connessione') }],
        warnings: []
      });
      return { valid: false, errors: [{ code: 'NETWORK_ERROR', message: error.message }] };
    }
  }, [state.selections, state.betType, state.amounts, state.systemConfig, state.meta.context, setValidation, updateCalculations]);

  // Submit ticket
  const submitTicket = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_META, payload: { isSubmitting: true } });

    try {
      const userId = localStorage.getItem('userId') || 
                     JSON.parse(localStorage.getItem('CLIENT_SESSION') || '{}')?.userId;
      
      const ticketId = state.meta.ticketId || uuidv4();

      const response = await fetch(`${API_URL}/api/bets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        },
        body: JSON.stringify({
          ticketId,
          userId,
          selections: state.selections,
          betType: state.betType.toUpperCase(),
          amount: state.amounts.totalStake || state.amounts.stake,
          amountPerCombination: state.amounts.perCombination,
          systemConfig: state.systemConfig,
          context: state.meta.context,
          odds: state.calculations.totalOdds,
          potentialWinMin: state.calculations.potentialWin.min,
          potentialWinMax: state.calculations.potentialWin.max,
          numCombinations: state.calculations.combinations || 1
        })
      });

      const result = await response.json();

      dispatch({ type: ACTIONS.SET_META, payload: { isSubmitting: false } });

      if (response.ok) {
        if (result.status === 'accepted') {
          clearSelections();
        } else if (result.status === 'pending_approval') {
          setValidation({ status: 'pending_approval' });
        }
        return result;
      } else {
        setValidation({
          status: 'invalid',
          errors: [{ code: 'SUBMIT_ERROR', message: result.error || i18next.t('Errore durante il salvataggio') }]
        });
        return { status: 'rejected', error: result.error };
      }
    } catch (error) {
      dispatch({ type: ACTIONS.SET_META, payload: { isSubmitting: false } });
      console.error('Submit error:', error);
      return { status: 'rejected', error: error.message };
    }
  }, [state, clearSelections, setValidation]);

  // Check if selection exists
  const hasSelection = useCallback((eventId, marketId, outcomeId) => {
    return state.selections.some(
      s => s.eventId === eventId && 
           s.market.id === marketId && 
           s.outcome.id === outcomeId
    );
  }, [state.selections]);

  // Toggle selection (add if not exists, remove if exists)
  const toggleSelection = useCallback((selection) => {
    const exists = hasSelection(
      selection.eventId, 
      selection.market.id, 
      selection.outcome.id
    );
    
    if (exists) {
      removeSelection(selection.eventId, selection.market.id, selection.outcome.id);
    } else {
      addSelection(selection);
    }
  }, [hasSelection, addSelection, removeSelection]);

  const value = {
    state,
    actions: {
      addSelection,
      removeSelection,
      updateSelectionOdds,
      clearSelections,
      setBetType,
      setSystemConfig,
      setAmount,
      setSystemAmount,
      updateCalculations,
      setValidation,
      validateTicket,
      submitTicket,
      hasSelection,
      toggleSelection
    }
  };

  return (
    <BetslipContext.Provider value={value}>
      {children}
    </BetslipContext.Provider>
  );
}

// Custom hook to use betslip context
export function useBetslip() {
  const context = useContext(BetslipContext);
  if (!context) {
    throw new Error(i18next.t('useBetslip must be used within a BetslipProvider'));
  }
  return context;
}

// Export for testing
export { ACTIONS, initialState, betslipReducer, determineContext };
